import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useApplication, usePublicConfig, type ApplicationInput } from '../context';
import type { ApplicationStatusResponse } from '../services';

const applicationSchema = yup
  .object({
    fullName: yup.string().required('Full name is required'),
    phone: yup
      .string()
      .matches(/^(?:\+254|0)\d{9}$/, 'Use format 07XXXXXXXX or +2547XXXXXXXX')
      .required('Phone number is required'),
    idNumber: yup
      .string()
      .matches(/^[0-9]{6,10}$/, 'National ID should be 6 to 10 digits')
      .required('National ID is required'),
    email: yup
      .string()
      .email('Enter a valid email address')
      .optional()
      .default('')
      .nullable()
      .transform((value) => value ?? ''),
    reason: yup
      .string()
      .min(20, 'Tell us a bit more (at least 20 characters)')
      .required('Please share why you want to join'),
  })
  .required();

type ApplicationFormValues = yup.InferType<typeof applicationSchema>;

export const ApplicationPage = () => {
  const [formError, setFormError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusResult, setStatusResult] = useState<ApplicationStatusResponse | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const navigate = useNavigate();
  const {
    submitApplication,
    submitting,
    error: applicationError,
    clearError,
    lastSubmittedPhone,
    checkStatus,
  } = useApplication();
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';

  const [statusPhone, setStatusPhone] = useState(() => lastSubmittedPhone ?? '');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ApplicationFormValues>({
    resolver: yupResolver(applicationSchema),
    defaultValues: {
      fullName: '',
      phone: '',
      idNumber: '',
      email: '',
      reason: '',
    },
  });

  useEffect(() => {
    clearError();
    setFormError(null);
    setStatusError(null);
    setStatusResult(null);
  }, [clearError]);

  useEffect(() => {
    if (lastSubmittedPhone) {
      setStatusPhone(lastSubmittedPhone);
    }
  }, [lastSubmittedPhone]);

  const onSubmit = async (values: ApplicationFormValues) => {
    setFormError(null);
    clearError();
    setStatusResult(null);

    const payload: ApplicationInput = {
      fullName: values.fullName.trim(),
      phone: values.phone.trim(),
      idNumber: values.idNumber.trim(),
      email: values.email?.trim() || undefined,
      reason: values.reason.trim(),
    };

    try {
      await submitApplication(payload);
      reset({ fullName: '', phone: '', idNumber: '', email: '', reason: '' });
      navigate('/apply/success', { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Application submission failed. Please try again.';
      setFormError(message);
    }
  };

  const displayError = formError ?? applicationError;
  const isProcessing = isSubmitting || submitting;
  const statusHelperText = useMemo(() => {
    if (statusResult) {
      return null;
    }
    return 'We will look up the latest update using the phone number you submitted with.';
  }, [statusResult]);

  const handleStatusCheck = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusError(null);
    setStatusResult(null);

    const trimmedPhone = statusPhone.trim();

    if (!trimmedPhone) {
      setStatusError('Enter the phone number you used for your application.');
      return;
    }

    setCheckingStatus(true);
    clearError();

    try {
      const result = await checkStatus(trimmedPhone);
      setStatusResult(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to retrieve status. Please try again.';
      setStatusError(message);
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <section className="relative flex min-h-screen w-full flex-col bg-slate-900 text-slate-100 lg:flex-row">
      <div
        className="fixed inset-x-0 top-0 h-[48vh] w-full overflow-hidden lg:hidden"
        style={{ backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-[calc(12vh+env(safe-area-inset-bottom))] pt-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            Join {chamaName}
          </span>
          <h1 className="mt-3 font-display text-3xl leading-snug text-white">
            Become part of our chama.
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-brand-100/80">
            Submit your details and a short motivation. We respond within seven days via SMS.
          </p>
        </div>
      </div>

      <div
        className="relative hidden flex-1 items-center justify-center px-16 py-20 lg:flex"
        style={{ backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 max-w-md space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            Join {chamaName}
          </span>
          <h1 className="font-display text-4xl leading-tight text-white">
            Application to become a {chamaName} member
          </h1>
          <p className="text-base leading-relaxed text-brand-100/70">
            Provide your contact details and a short motivation. The committee reviews applications weekly and will reach out via SMS once a decision is made.
          </p>
          <ul className="space-y-3 text-sm text-brand-100/80">
            <li className="rounded-2xl border border-white/20 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Requirements</p>
              <p className="mt-1">Active M-Pesa line, government-issued ID, and commitment to monthly contributions.</p>
            </li>
            <li className="rounded-2xl border border-white/20 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Next steps</p>
              <p className="mt-1">Approved applicants receive onboarding instructions and a temporary password via SMS.</p>
            </li>
            <li className="rounded-2xl border border-white/20 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Already part of {chamaName}?</p>
              <p className="mt-1">
                <Link to="/login" className="font-semibold text-brand-100 underline decoration-brand-100/60">
                  Sign in here
                </Link>
                .
              </p>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-[48vh] flex flex-1 items-end justify-center bg-transparent pb-8 lg:mt-0 lg:min-h-screen lg:items-center lg:bg-white lg:px-8 lg:py-16 lg:text-slate-900 dark:lg:bg-slate-950 dark:lg:text-slate-100">
        <div className="fixed inset-x-0 bottom-0 z-10 w-full rounded-t-[2.5rem] bg-white px-6 py-10 text-slate-900 shadow-2xl dark:bg-slate-950 dark:text-slate-100 max-h-[80vh] overflow-y-auto pb-[calc(16px+env(safe-area-inset-bottom))] lg:static lg:max-w-md lg:rounded-none lg:px-10 lg:py-16 lg:shadow-none lg:max-h-none lg:overflow-visible">
          <div className="mb-10 text-center lg:text-left">
            <h2 className="font-display text-3xl text-slate-900 dark:text-white">Application form</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              Share your details and why you would like to join {chamaName}.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Full name
              </label>
              <input
                id="fullName"
                type="text"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...register('fullName')}
              />
              {errors.fullName ? <p className="mt-2 text-sm text-red-500">{errors.fullName.message}</p> : null}
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="07XXXXXXXX"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  {...register('phone')}
                />
                {errors.phone ? <p className="mt-2 text-sm text-red-500">{errors.phone.message}</p> : null}
              </div>
              <div>
                <label htmlFor="idNumber" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  National ID number
                </label>
                <input
                  id="idNumber"
                  type="text"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  {...register('idNumber')}
                />
                {errors.idNumber ? <p className="mt-2 text-sm text-red-500">{errors.idNumber.message}</p> : null}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Email (optional)
              </label>
              <input
                id="email"
                type="email"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...register('email')}
              />
              {errors.email ? <p className="mt-2 text-sm text-red-500">{errors.email.message}</p> : null}
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Why do you want to join {chamaName}?
              </label>
              <textarea
                id="reason"
                rows={4}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...register('reason')}
              />
              {errors.reason ? <p className="mt-2 text-sm text-red-500">{errors.reason.message}</p> : null}
            </div>

            <button
              type="submit"
              disabled={isProcessing}
              className="inline-flex w-full items-center justify-center rounded-xl bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
            >
              {isProcessing ? 'Submitting...' : 'Submit application'}
            </button>

            {displayError ? (
              <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                {displayError}
              </p>
            ) : null}

          </form>

          <div className="mt-10 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">Check your application status</h3>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{statusHelperText}</p>
              <form onSubmit={handleStatusCheck} className="mt-4 space-y-4" noValidate>
                <div>
                  <label
                    htmlFor="statusPhone"
                    className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300"
                  >
                    Phone number
                  </label>
                  <input
                    id="statusPhone"
                    type="tel"
                    value={statusPhone}
                    onChange={(event) => setStatusPhone(event.target.value)}
                    placeholder="+2547XXXXXXXX"
                    className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  />
                </div>

                <button
                  type="submit"
                  disabled={checkingStatus}
                  className="inline-flex w-full items-center justify-center rounded-full bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
                >
                  {checkingStatus ? 'Checking status…' : 'Check status'}
                </button>
              </form>

              {statusError || applicationError ? (
                <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                  {statusError ?? applicationError}
                </p>
              ) : null}

              {statusResult ? (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                  <p className="font-semibold">Status: {statusResult.status.replace(/_/g, ' ')}</p>
                  <p className="mt-1 text-xs uppercase tracking-wide text-emerald-600/80 dark:text-emerald-200/70">Message</p>
                  <p className="text-sm">{statusResult.message}</p>
                  <p className="mt-2 text-xs text-emerald-600/80 dark:text-emerald-200/70">
                    Applied on {new Date(statusResult.date_applied).toLocaleString()}
                  </p>
                  {statusResult.date_joined ? (
                    <p className="mt-1 text-xs text-emerald-600/80 dark:text-emerald-200/70">
                      Joined on {new Date(statusResult.date_joined).toLocaleString()}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-300">
              Already a member?{' '}
              <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300">
                Sign in here
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

